<template>
  <div class="space-y-4 p-5">
    <div class="space-y-4">
      <div>
        <p class="text-black text-sm font-medium">Device group</p>
        <p class="text-xs pointer-events-none">Enter the name of the device group.</p>
      </div>
      <div v-if="preset.deviceGroup" class="flex flex-wrap gap-2 mt-2">
        <span
          class="bg-tertiary-50 text-tertiary-500 px-4 py-2 rounded-lg flex items-center text-sm font-medium"
        >
          {{ preset.deviceGroup }}
          <button class="ml-2 text-tertiary-500" @click="reset">✕</button>
        </span>
      </div>
      <TextInput
        v-if="!preset.deviceGroup"
        class="w-96"
        @enter="(deviceGroup) => acceptDeviceGroup(deviceGroup)"
      >
        <template #suffix="{ value }">
          <button
            class="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-primary-500 px-3 py-2 text-xs text-white shadow-md transition hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-400"
            @click="acceptDeviceGroup(value)"
          >
            Enter
            <ArrowTurnDownLeftIcon class="w-4 h-4 text-white" />
          </button>
        </template>
      </TextInput>
    </div>
    <div v-if="isFindingAlternativeDeviceGroupNames">
      <Loader message="Finding alternative device group names..." />
    </div>
    <div v-if="preset.deviceGroup && !isFindingAlternativeDeviceGroupNames">
      <div class="flex items-center justify-between">
        <div>
          <span class="text-sm text-black font-medium pointer-events-none">
            Alternative device group names
          </span>
          <p class="text-xs pointer-events-none">
            Which other names are commonly used for the device group?
          </p>
        </div>
      </div>

      <div class="flex flex-wrap gap-2 mt-2">
        <span
          v-for="name in preset.alternativeDeviceGroupNames"
          :key="name"
          class="px-3 py-1 rounded-lg flex items-center text-sm font-medium bg-tertiary-50 text-tertiary-500"
        >
          {{ name }}

          <button class="ml-2 text-red-500" @click="removeAlternativeName(name)">✕</button>
        </span>

        <TextInput
          v-model="newAlternativeName"
          class="w-96"
          placeholder="Enter alternative device group name"
          @enter="addAlternativeName"
        >
          <template #suffix>
            <button
              class="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-primary-500 px-3 py-2 text-xs text-white shadow-md transition hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-400"
              @click="addAlternativeName"
            >
              Enter
              <ArrowTurnDownLeftIcon class="w-4 h-4 text-white" />
            </button>
          </template>
        </TextInput>
      </div>
      <button
        v-if="!hasAcceptedAlternativeNames"
        class="px-2 py-1 bg-primary-500 text-white rounded-md mt-4"
        @click="acceptAlternativeDeviceGroupNames"
      >
        Accept
      </button>
    </div>

    <div v-if="isDeterminingLOE">
      <Loader message="Determining level of evidence..." />
    </div>
    <div v-if="preset.deviceGroup && hasAcceptedAlternativeNames && !isDeterminingLOE">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm text-black font-medium pointer-events-none">Target LOE</p>
          <p class="text-xs pointer-events-none">
            To investigate the medical background of {device_group}, we recommend considering the
            following level of evidence. Do you agree?
          </p>
        </div>
      </div>

      <div class="mt-2">
        <Pyramid
          v-model="preset.targetLoe"
          :segments="[
            {
              level: 60,
              label: 'Evidence-based clinical guidelines',
              color: '#06b6d4',
              id: 'a',
            },
            {
              level: 70,
              label: 'Systematic reviews and meta-analyses',
              color: '#84cc16',
              id: 'b',
            },
            {
              level: 80,
              label: 'Randomized controlled trials',
              color: '#eab308',
              id: 'c',
            },
            {
              level: 90,
              label: 'Observational studies and cohort studies',
              color: '#fb923c',
              id: 'd',
            },
            {
              level: 100,
              label:
                'Case studies, case series, mechanistic reasoning, narrative sources (reviews, textbooks, expert opinion)',
              color: '#f472b6',
              id: 'e',
            },
          ]"
        />
      </div>
      <button
        v-if="!hasAcceptedTargetLOE"
        class="px-2 py-1 rounded-md mt-4"
        :class="{
          'bg-gray-300 text-gray-100 cursor-not-allowed': !preset.targetLoe,
          'bg-primary-500 text-white': preset.targetLoe,
        }"
        :disabled="!preset.targetLoe"
        @click="acceptTargetLOE"
      >
        Accept
      </button>
    </div>
    <div v-if="isDeterminingTimeFrame">
      <Loader message=" Determining time frame..." />
    </div>
    <div
      v-if="
        preset.deviceGroup &&
        hasAcceptedAlternativeNames &&
        hasAcceptedTargetLOE &&
        !isDeterminingTimeFrame
      "
    >
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm text-black font-medium pointer-events-none">Time range</p>
          <p class="text-xs pointer-events-none">
            To investigate the medical background of {procedure_name}, we recommend considering the
            following time range. Do you agree?
          </p>
        </div>
      </div>
      <div class="space-y-6 sm:flex sm:items-center sm:space-y-0 sm:space-x-10 mt-2">
        <div
          v-for="range in [
            {
              value: 1,
              label: '1 year',
            },
            {
              value: 5,
              label: '5 years',
            },
            {
              value: 10,
              label: '10 years',
            },
          ]"
          :key="range.value"
          class="flex items-center"
        >
          <input
            :id="range.value.toString()"
            v-model="preset.timeRange"
            :value="range.value"
            name="time-range"
            type="radio"
            class="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white not-checked:before:hidden checked:border-primary-600 checked:bg-primary-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden"
          />
          <label
            :for="range.value.toString()"
            class="ml-3 block text-sm/6 font-medium text-gray-900"
          >
            {{ range.label }}
          </label>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, watch } from "vue";
import { ArrowTurnDownLeftIcon } from "@heroicons/vue/24/outline";
import TextInput from "@app/components/Global/Inputs/TextInput.vue";
import Pyramid from "@app/components/Global/Pyramid.vue";
import { injectStrict } from "@app/utils/injectStrict";
import { generateTimeLimitRange } from "@app/utils/timeLimits";
import { useNotifiedBodyPresetAssistant } from "../MedicalBackgroundPreset/assistant";
import { CreateReviewKey } from "../../../useCreateProjectReview";
import Loader from "@app/components/Loader.vue";

const { findDeviceGroupNames, determineTimeFrame, generateLevelOfEvidence } =
  useNotifiedBodyPresetAssistant();

const { preset } = injectStrict(CreateReviewKey);

const hasAcceptedAlternativeNames = ref(false);
const hasAcceptedTargetLOE = ref(false);

const isFindingAlternativeDeviceGroupNames = ref(false);
const isDeterminingLOE = ref(false);
const isDeterminingTimeFrame = ref(false);

async function acceptDeviceGroup(deviceGroup: string) {
  preset.value.deviceGroup = deviceGroup;
  isFindingAlternativeDeviceGroupNames.value = true;
  preset.value.alternativeDeviceGroupNames = await findDeviceGroupNames({
    deviceGroup,
  }).catch(() => []);
  isFindingAlternativeDeviceGroupNames.value = false;
}

async function acceptAlternativeDeviceGroupNames() {
  hasAcceptedAlternativeNames.value = true;
  isDeterminingLOE.value = true;
  generateLevelOfEvidence({
    deviceGroupNames: [preset.value.deviceGroup, ...preset.value.alternativeDeviceGroupNames].join(
      ", ",
    ),
  })
    .then((loe) => {
      preset.value.targetLoe = loe;
    })
    .catch(() => {
      preset.value.targetLoe = "";
    })
    .finally(() => {
      isDeterminingLOE.value = false;
    });
}

async function acceptTargetLOE() {
  hasAcceptedTargetLOE.value = true;
  isDeterminingTimeFrame.value = true;
  preset.value.timeRange = await determineTimeFrame({
    deviceGroupNames: [preset.value.deviceGroup, ...preset.value.alternativeDeviceGroupNames].join(
      ", ",
    ),
  }).catch(() => undefined);
  isDeterminingTimeFrame.value = false;
}

function reset() {
  preset.value.deviceGroup = "";
  preset.value.alternativeDeviceGroupNames = [];
  preset.value.targetLoe = "";
  preset.value.timeRange = undefined;
  hasAcceptedAlternativeNames.value = false;
  hasAcceptedTargetLOE.value = false;
  isFindingAlternativeDeviceGroupNames.value = false;
  isDeterminingLOE.value = false;
  isDeterminingTimeFrame.value = false;
  preset.value.timeLimitFromDate = undefined;
  preset.value.timeLimitToDate = undefined;
  preset.value.timeRange = undefined;
}

const newAlternativeName = ref("");

function addAlternativeName() {
  const name = newAlternativeName.value.trim();
  if (!name || preset.value.alternativeDeviceGroupNames.includes(name)) return;

  preset.value.alternativeDeviceGroupNames.push(name);

  newAlternativeName.value = "";
}

function removeAlternativeName(name: string) {
  preset.value.alternativeDeviceGroupNames = preset.value.alternativeDeviceGroupNames.filter(
    (n) => n !== name,
  );
}

watch(
  () => preset.value.timeRange,
  (newValue) => {
    if (newValue) {
      const range = generateTimeLimitRange(newValue);
      if (!range) return;
      preset.value.timeLimitFromDate = range[0];
      preset.value.timeLimitToDate = range[1];
    }
  },
);
</script>
